import dayjs from 'dayjs'

// Health score enums - duplicated here to avoid circular dependencies
export enum ComponentHealthCondition {
  Excellent = 'Excellent',
  Fair = 'Fair',
  Bad = 'Bad',
  VeryPoor = 'Very Poor',
}

export enum NoRLHealthScore {
  NoInspection = 'HealthScore:NoInspection',
  NotEnoughData = 'HealthScore:NotEnoughData',
}

export type CentralizedHealthScore = ComponentHealthCondition | NoRLHealthScore

import {
  GeckoAssetHealthAction,
  GeckoAssetHealthPlan,
  GeckoAssetModel,
  GeckoEntryBase,
  GeckoInspection,
  GeckoMarkupLayer,
  GeckoTMinMethod,
  GeckoUnit,
  RolodexEntryJson,
  YearString,
} from 'types'
import { CustomCorrosionRegion } from 'types/fde/simulationScenarioInterfaces'
import { RolodexEntryType } from 'types/rolodex/rolodexEntries'
import { DeficiencyRiskData } from 'utils/fde/displayedRisk'

import { ProjectCostData } from './costs'
import { Scenario } from './scenario'
import { TimeDuration, TimeUnit } from './timeDuration'

export interface FailureProbability {
  failureProbability: number
  failureType?: string
  startYear?: YearString
  endYear?: YearString
}

export interface PlanAndActions {
  plan: GeckoAssetHealthPlan
  actions: HealthPlanAction[]
}

export interface LinkedHealthPlanAction {
  id: string
  type: RolodexEntryType
  markupLayers?: GeckoMarkupLayer[]
  data: {
    description?: string
    name?: string
    scenarios?: Scenario[]
    regions?: CustomCorrosionRegion[]
    fillHeight?: number
    specificGravity?: number
    cost?: ProjectCostData
    customCorrosionRateDuration?: TimeDuration
    wrapRegions?: PipeWrapRegion[]
  }
}

export type HealthPlanActionData = {
  area?: number
  costTotal: number | null
  minRl?: number
  linkedEntry?: LinkedHealthPlanAction
  actionType?: string
  componentId?: string
  componentName?: string
  componentType?: string
}

export interface HealthPlanAction {
  action: GeckoAssetHealthAction
  actionData: HealthPlanActionData
}

export interface AssetOutageConfig {
  assetOutage: GeckoEntryBase<AssetOutage> | undefined
  yearOfRetirement: string | undefined
}

/**
 * This is only a partial definition of this entry type, these are the fields that are used in the context of the AHP
 */
export type FDECustomerUnit = {
  year_of_retirement?: string
  default_repair_setup_cost?: number
  default_repair_cost_per_sqft?: number
  opportunity_cost_discount?: number
}

export type AssetOutage = {
  outage_start_date: string
  outage_interval: number
  outage_interval_unit: TimeUnit
  outage_length: number
  outage_length_unit: TimeUnit
  created_date: string
}

export type RepairRect = {
  xStart: number
  xEnd: number
  yStart: number
  yEnd: number
  xUnit: string
  yUnit: string
}

export type AhpScenario = {
  componentHealthPlanActionId: string
  spanToDate: string // date which the outputs need to be generated till
  customCorrosionRegions?: CustomCorrosionRegion[]
  tminMethod: GeckoTMinMethod
  repairRects?: RepairRect[]
  fillHeight?: number
  specificGravity?: number
  wrapRegions?: PipeWrapRegion[]
}

/**
 * A 4D array containing the calculations over an asset health plan to show remaining life extension:
 * It's comprised of an array of Component Health Plan actions, where for each action array we have:
 * - A 2D array of thickness
 * - A 2D array of corrosion loss
 * - A 2D array of Tmin
 * - The year
 * -
 */
export type AssetHealthDataFrame = AssetHealthDataLayers & {
  componentHealthPlanActionId: string
  spanToDate: string // Using YYYY-MM-DD format
  assetModel: RolodexEntryJson
}

export type AssetHealthDataLayers = {
  thickness: number[][]
  lpy: number[][]
  tmin: number[][]
  rl: number[][]
}

export enum InterfaceViewType {
  SOURCE = 'source',
  RESULT = 'result',
}

export interface Outage {
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
}

/**
 * Encodes the remaining life at a point in the lifetime of an Asset Health Plan
 * Used for calculating added years and an asset's failure date
 */
export interface AssetHealthPlanRemainingLifeData {
  componentHealthPlanActionId?: string
  date: dayjs.Dayjs
  remainingLife: number
  remainingLifeAdded?: number
}

/**
 * A region that is wrapped with a material (such as fiber glass) to increase the stress tolerance of a pipe
 * NOTE - current the geometry of regions are only parameterized by a start and end on the elevation of the pipe
 */
export interface PipeWrapRegion {
  id: string
  regionStart: number
  regionEnd: number
  measurementUnit: string
  addedStressTolerance: number
}

export interface PipeWrap {
  name: string
  create_time: number
  wrapRegions: PipeWrapRegion[]
  cost?: ProjectCostData
}

export interface OutageGraphWindow {
  start: number
  end: number
}

/**
 * Used to describe the types of actions executable in component health plans
 */
export interface ComponentHealthPlanActionType {
  shortName: string
  longName: string
  description?: string
}

export interface ReplacementPlan {
  name: string
  create_time: number
  cost?: ProjectCostData
}

export type DamageInfo = {
  [year: string]: {
    damageAreas: DamageCategory[] | undefined
    riskScore: number | undefined
    healthScore: number | undefined
    minRl: number | undefined
    percentBelowTmin: number | undefined
  }
}

export type DamageInfoWithHealthCondition = {
  [year: string]: {
    damageAreas: DamageCategory[] | undefined
    riskScore: number | undefined
    healthScore: number | undefined
    minRl: number | undefined
    percentBelowTmin: number | undefined
    componentHealthCondition: CentralizedHealthScore
  }
}

export type BulkDamageAreas = {
  [componentId: string]: {
    [planKey: string | 'baseData']: DamageInfo | ErrorInfo
  }
}

export type BulkDamageAreasWithHealthConditions = {
  [componentId: string]: {
    [planKey: string | 'baseData']: DamageInfoWithHealthCondition | ErrorInfo
  }
}

export type RemainingLifeInfo = {
  rl: {
    minRl: number
  }
  counts: {
    totalCount: number
    belowTminCount: number
    percentBelowTmin: number
  }
}

export type ErrorInfo = {
  error: string
}

export type PlanMinRlsInfo = {
  [actionId: string]: {
    minRl: number
    maxRl: number
  }
}

export interface AggregateHealthPlanComponentData {
  componentName?: string
  componentType?: string
  lastInspectionDate: string
  minRL?: number
  percentBelowTmin?: number
  componentId: string
  componentDamageAreas?: {
    [planKey: string]: DamageInfo | ErrorInfo
  }
  preferredPlanDamageAreas?: {
    [year: string]: {
      damageAreas: DamageCategory[] | undefined
      riskScore: number | undefined
      healthScore: number | undefined
    }
  }
}

export interface UnitRiskAssessment {
  conditionSummary?: string
  causes?: string
  consequences?: string
  existingControls?: string
  evaluationRationaleJustification?: string
  evaluationRationaleCost?: number
  likelihood?: string
  velocity?: string
  riskDate?: number
  predictedRisk?: number
  nextReview?: string
  mitigatingAction?: string
  currentRisk?: number
  currentLikelihood?: number
  customerProjectId?: string
  /** Deficiency risk data from component deficiencies linked to this risk assessment */
  deficiencies?: DeficiencyRiskData[]
}

export interface AggregatedHealthPlanDataByUnit {
  unitName: string
  components: Map<string, AggregateHealthPlanComponentData>
  criticality: string
  unitRaw?: GeckoUnit
  plans: HealthPlanData[]
  assetModel: GeckoAssetModel | undefined
  area: string
  preferredNextActionDate?: string
  preferredPlanCost?: number
  lastInspectionDate: string
  lastInspection?: GeckoInspection
  riskAssessment?: UnitRiskAssessment
  unitHealthScore: CentralizedHealthScore
}

export interface HealthPlanData {
  planName: string
  unitName?: string
  actionCount: number
  preferred: boolean
  planId: string
  planCost: number
  planActions: HealthPlanAction[]
  planRaw: GeckoAssetHealthPlan
}

export interface AggregatedHealthPlanDataPayload {
  aggregatedChpData: AggregatedHealthPlanDataByUnit[]
  isLoading: boolean
  isRiskCalculatorLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => Promise<void>
}

export const RD_TYPE_DAMAGE_AREA = RolodexEntryType.INDICATION_DAMAGE_AREA

export enum DamageCategoryOptions {
  RUPTURE = 'Rupture',
  PINHOLE_LEAK = 'Pinhole_Leak',
  NONE = 'None',
  UNKNOWN = 'Unknown',
}

export type GeneratedDamageCategory = {
  version: string
  area?: number
  percentInRange?: number
  elevationRatio?: number
  likelihood?: number
} & DamageCategoryData

export type DamageCategoryData = {
  category?: DamageCategoryOptions
  confidence?: number
  probability?: number
  consequence?: number
  corrosionRate?: number
}

export type DamageCategory = {
  generated: GeneratedDamageCategory
  selection: {
    x: [number, number]
    y: [number, number]
  }
} & DamageCategoryData
